'use strict';

import ModalManager from "../../js/components/modalManager";
import SliderManager from "../../js/components/sliderManager";
import CustomSlider from "../../custom-slider/src/customSlider";

export default class ImageGallery extends HTMLElement {
    constructor() {
        super();

        this.columnRang = [
            [0, 0],
            [1, 2],
            [3, 4],
            [5, 6]
        ];
        this.newColumnRang = [
            [0, 1],
            [2, 3],
            [4, 5]
        ];

        this.numImageOverText = [2, 4, 6];
        this.newNumImageOverText = [3, 5];

        this.images = null;
        this.defaultImage = this.getAttribute('defaultImage');
        this.stylesheet = this.getAttribute('stylesheet');
        this.modalID = this.getAttribute('modalID');
        this.itemClickListener = this.getAttribute('item-click-listener');
        this.translations = this.getAttribute('translations');
        this.imageSize = this.getAttribute('image-size');
        this.totalImages = 0;
        this.modalManager = new ModalManager();
        this.customSlider = new CustomSlider();
        this.initialized = false;
        this.newColumnSystem = this.getAttribute('new-column-system');
        this.roundImageCorners = this.getAttribute('round-image-corners');
    }

    static get observedAttributes() {
        return ['images', 'defaultImage', 'stylesheet', 'modalID'];
    }

    connectedCallback() {
        this._shadowRoot = this.attachShadow({mode: 'open'});
        const t = document.querySelector('#image-gallery');
        const instance = t.content.cloneNode(true);
        this.images = JSON.parse(this.getAttribute('images'));
        this.imagesArray = [];
        for (const images of this.images) {
            this.imagesArray = this.imagesArray.concat(images.img);
        }
        this.totalImages = this.imagesArray.length;
        this.translations = JSON.parse(this.translations);
        this.createColumns(instance);
        instance.querySelector('[name="stylesheet"]').href = this.stylesheet;
        this._shadowRoot.appendChild(instance);
        return this._shadowRoot;
    }

    initializeSwiper(swiper) {
        this.swiper = swiper;
        this.sliderManager = new SliderManager(swiper);
        this.generateSlider();
        this.initializeLazyLoad();
    }

    _buildSlider() {
        let imageElement;
        let imageElements = [];
        let imageGallerySlider = this._shadowRoot.querySelector('[name="image-gallery-slider"]');
        if (!imageGallerySlider) {
            return;
        }

        if (this.imageSize) {
            imageGallerySlider.classList.add(this.imageSize);
        }
        for (let image of this.imagesArray) {
            imageElement = document.createElement('img');
            imageElement.setAttribute('src', image);
            imageElement.setAttribute('loading', 'lazy');
            imageElements.push(imageElement)
        }

        let slider = this.customSlider.buildSlider(imageElements, this._onClickSlider.bind(this), true);
        imageGallerySlider.appendChild(slider);

        return this.sliderManager.addSlider(
            imageGallerySlider,
            {
                slidesPerView: 1,
                pagination: true,
                centerSlides: true
            }
        );
    }

    _onClickSlider(sliderId) {
        this.modalManager.openModal(this.modalID, typeof sliderId == 'undefined' || !sliderId || sliderId < 0);

        let imageGalleryItemClickListener = document.querySelector(this.itemClickListener);
        if (imageGalleryItemClickListener) {
            imageGalleryItemClickListener.onShow(sliderId);
        }
    }

    createColumns(instance) {
        let imageGallery = instance.querySelector('[name="image-gallery"]');
        let currentColumnElement = null;
        let currentImageElement = null;
        let numImages = 0;

        if (this.newColumnSystem) {
            this.columnRang = this.newColumnRang;
        }

        for (let [key, rang] of this.columnRang.entries()) {
            currentColumnElement = this.createColumnElement(key + 1);
            for (let i = rang[0]; i <= rang[1]; i++) {
                currentImageElement = this.getCurrentImageElement(
                    numImages,
                    (key === this.columnRang.length - 1) && (i === rang[1])
                );
                currentColumnElement.appendChild(currentImageElement);
                numImages++;
            }
            imageGallery.appendChild(currentColumnElement);
        }
    }

    getCurrentImageElement(numImages, lastImage) {
        let imageOverText = false;
        if (this.newColumnSystem) {
            if (this.newNumImageOverText.indexOf(numImages) >= 0) {
                imageOverText = true;
            }
        } else {
            if (this.numImageOverText.indexOf(numImages) >= 0) {
                imageOverText = true;
            }
        }

        let currentImageElement = this.createImageElement(numImages, lastImage);
        let image = this.getImage(this.imagesArray[numImages]);

        let imageElement = document.createElement('img');
        imageElement.setAttribute('data-path', image);
        imageElement.setAttribute('data-entity', 'image');

        currentImageElement.appendChild(imageElement);

        if (this.imagesArray.length === 0) {
            return currentImageElement;
        }

        if (imageOverText && this.itemClickListener != null) {
            let showAllImages = this.translations.hasOwnProperty('showAllImages')
                ? this.translations.showAllImages
                : null;
            if (showAllImages) {
                let imageTextContainer = document.createElement('div');
                imageTextContainer.classList.add('image-container-text');
                imageTextContainer.innerHTML = showAllImages.replace("@num@", this.totalImages);

                imageTextContainer.addEventListener("click", (event) => {
                    this._onClickSlider(null);
                    event.stopPropagation();
                });

                currentImageElement.appendChild(imageTextContainer);
            }
        }

        if (this.modalID) {
            currentImageElement.addEventListener("click", (event) => {
                this._onClickSlider(numImages);
            });
        }

        return currentImageElement;
    }

    getImage(image) {
        if (typeof image !== 'undefined') {
            return image;
        }
        return this.defaultImage;
    }

    createImageElement(currentImage, lastImage = false) {
        let clickable = this.itemClickListener;
        let currentImageElement = document.createElement('div');
        if (clickable === null || this.imagesArray.length === 0) {
            currentImageElement.classList.add('no-pointer');
        }
        currentImageElement.classList.add('image-container');
        if (this.roundImageCorners) {
            currentImageElement.classList.add('rounded');
        }

        if (currentImage && !lastImage) {
            currentImageElement.setAttribute('num', currentImage);
        }

        return currentImageElement;
    }

    createColumnElement(column) {
        let currentColumnElement = document.createElement('div');
        if (this.newColumnSystem) {
            currentColumnElement.classList.add('column-new');
        } else {
            currentColumnElement.classList.add('column');
        }
        currentColumnElement.setAttribute('name', 'column-' + column);

        return currentColumnElement;
    }

    initializeLazyLoad() {
        this._shadowRoot.querySelectorAll('[data-entity="image"]').forEach(image => {
            const imageUrl = image.getAttribute('data-path');
            image.setAttribute('src', imageUrl);
            image.setAttribute('loading', 'lazy');
        });
        return true;
    }

    generateSlider() {
        this.sliderElement = this._buildSlider();

        let galleryContainer = document.querySelector('#gallery-container');

        let img = this._shadowRoot.querySelector('[name="image-gallery-slider"] [name="slider"] img');
        if (img) {
            img.addEventListener('load', () => {
                if (galleryContainer) {
                    galleryContainer.style.minHeight = 'initial';
                }

                // Allow safari to show paginator bullets on first load.
                this.sliderElement.slideTo(1);
                this.sliderElement.slideTo(0);
            });
        }
        this.initialized = true;
        let event = new Event('image-gallery-initialized');
        this.dispatchEvent(event);
    }
}
